import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import StatusSelect from "@/components/StatusSelect";
import Attachments from "@/components/Attachments";
import TaskComments from "@/components/TaskComments";
import { Edit2, Loader2, History as HistoryIcon, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_BADGE = {
    high: "bg-red-50 text-red-700 border-red-200",
    medium: "bg-amber-50 text-amber-700 border-amber-200",
    low: "bg-slate-100 text-slate-700 border-slate-200",
};

function formatDate(d) {
    if (!d) return "—";
    try {
        return new Date(d).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return d;
    }
}

function Field({ label, children }) {
    return (
        <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                {label}
            </div>
            <div className="mt-0.5 text-sm font-medium text-foreground">{children}</div>
        </div>
    );
}

function Flag({ on, label }) {
    return (
        <span className="inline-flex items-center gap-1 text-sm">
            {on ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            ) : (
                <XCircle className="h-4 w-4 text-muted-foreground" />
            )}
            {label}
        </span>
    );
}

export default function TaskDetailDialog({ open, onOpenChange, task, onEdit, onChanged }) {
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [status, setStatus] = useState(task?.status);

    useEffect(() => { setStatus(task?.status); }, [task?.status]);

    const changeStatus = async (v) => {
        const prev = status;
        setStatus(v);
        try {
            await api.patch(`/tasks/${task.id}`, { status: v });
            onChanged?.();
        } catch (e) {
            setStatus(prev);
            toast.error(formatApiError(e));
        }
    };

    useEffect(() => {
        if (!open || !task?.id) return;
        setShowHistory(false);
        setHistoryLoading(true);
        api.get(`/tasks/${task.id}/history`)
            .then((r) => setHistory(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setHistoryLoading(false));
    }, [open, task?.id]);

    if (!task) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="task-detail-dialog">
                <DialogHeader>
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">
                                {task.client_name}
                            </DialogTitle>
                            <DialogDescription>{task.work_type}</DialogDescription>
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => onEdit?.(task)}
                            data-testid="task-detail-edit"
                            className="mr-10"
                        >
                            <Edit2 className="mr-1.5 h-4 w-4" /> Edit details
                        </Button>
                    </div>
                </DialogHeader>

                {/* Details */}
                <div className="grid grid-cols-2 gap-4 rounded-lg border border-border bg-card p-4 sm:grid-cols-3">
                    <Field label="Assigned to">{task.assigned_to_name || "—"}</Field>
                    <Field label="Status">
                        <StatusSelect value={status} onChange={changeStatus} />
                    </Field>
                    <Field label="Priority">
                        <span className={`sdc-pill border ${PRIORITY_BADGE[task.priority] || PRIORITY_BADGE.medium}`}>
                            {task.priority?.toUpperCase()}
                        </span>
                    </Field>
                    <Field label="Start date">{formatDate(task.task_date)}</Field>
                    <Field label="Due date">{formatDate(task.deadline_date)}</Field>
                    <Field label="Recurrence">
                        {task.recurrence && task.recurrence !== "none" ? task.recurrence : "—"}
                    </Field>
                </div>

                <div className="mt-3 flex flex-wrap gap-4">
                    <Flag on={task.billing_status} label="Billed" />
                    <Flag on={task.reimbursable_expense} label="Reimbursable" />
                    <Flag on={task.approved} label="Approved" />
                </div>

                {/* Description */}
                <div className="mt-4">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                        Description
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">
                        {task.remarks || (
                            <span className="text-muted-foreground">No description.</span>
                        )}
                    </p>
                </div>

                {/* Task-level attachments */}
                <Attachments taskId={task.id} />

                {/* Comments */}
                <TaskComments taskId={task.id} />

                {/* History */}
                <div className="mt-6">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowHistory((s) => !s)}
                        data-testid="task-detail-history-toggle"
                    >
                        <HistoryIcon className="mr-1.5 h-4 w-4" />
                        {showHistory ? "Hide" : "Show"} history ({history.length})
                    </Button>
                    {showHistory && (
                        historyLoading ? (
                            <div className="grid place-items-center py-6">
                                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                            </div>
                        ) : history.length === 0 ? (
                            <p className="mt-2 text-xs text-muted-foreground">
                                No changes recorded yet.
                            </p>
                        ) : (
                            <ol className="mt-2 space-y-3">
                                {history.map((h) => (
                                    <li
                                        key={h.id}
                                        className="rounded-lg border border-border bg-card p-3"
                                    >
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="font-semibold text-primary">{h.action}</span>
                                            <span className="text-muted-foreground">
                                                {new Date(h.created_at).toLocaleString("en-IN")}
                                            </span>
                                        </div>
                                        <div className="mt-1 text-sm font-medium">
                                            {h.user_name || h.user_email}
                                        </div>
                                        {h.changes && Object.keys(h.changes).length > 0 && (
                                            <ul className="mt-2 space-y-1">
                                                {Object.entries(h.changes).map(([k, v]) => (
                                                    <li
                                                        key={k}
                                                        className="rounded bg-secondary/60 px-2 py-1 text-xs"
                                                    >
                                                        <span className="font-semibold">{k}</span>:{" "}
                                                        <span className="text-muted-foreground">
                                                            {String(v?.from ?? "—")}
                                                        </span>{" "}
                                                        →{" "}
                                                        <span className="text-foreground">
                                                            {String(v?.to ?? "—")}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </li>
                                ))}
                            </ol>
                        )
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
